import { CreatorAccessory, PostType } from '@/types/creator';
import { LedPoint } from '../objects/LedPoint';
import { LedTape } from '../objects/LedTape';
import { ProjectRenderer } from '../ProjectRenderer';
import { AssetsLib } from '../utils/AssetsLib';
import { UUID } from '@/types/types';
import { Box3, Vector3 } from 'three';
import { ThreeEvent, UserDataProp } from '../types';
import { ObjectsManager } from './ObjectsManager';
import { LedAvailableSpace } from '../builders/HandleBuilder';

export class LedsManager {
	ledTapes: LedTape[] = [];
	ledPoints: LedPoint[] = [];
	private _autoPositioning = true;

	private assetsLib = AssetsLib.getInstance();

	constructor(
		private renderer: ProjectRenderer,
		private objectsManager: ObjectsManager
	) {}

	get autoPositioning() {
		return this._autoPositioning;
	}

	set autoPositioning(value: boolean) {
		this._autoPositioning = value;
		if (value) this.repositionLedPoints();
	}

	async initLedTapes(creatorAccessory: CreatorAccessory) {
		if (!creatorAccessory.variationAccessory.accessory.model3d?.model)
			throw new Error('MISSING_LED_TAPE_MODEL');

		const model = await this.assetsLib.getModel(
			creatorAccessory.variationAccessory.accessory.model3d.model.path
		);
		const ledTape = new LedTape(
			this.renderer,
			{ ...creatorAccessory, id: crypto.randomUUID() },
			model
		);

		this.ledTapes.push(ledTape);
		this.renderer.handleBuilder.buildLedTapes();
	}

	async addLedPoint(creatorAccessory: CreatorAccessory) {
		if (!creatorAccessory.variationAccessory.accessory.model3d?.model)
			throw new Error('MISSING_LED_POINT_MODEL');

		const model = await this.assetsLib.getModel(
			creatorAccessory.variationAccessory.accessory.model3d.model.path
		);

		model.traverse((child) => {
			const target = child.userData[UserDataProp.TARGET];
			if (!target) return;

			this.renderer.actionHandler.triggerLastAction(target);
		});
		const ledPoint = new LedPoint(this.renderer, creatorAccessory, model);

		this.ledPoints.push(ledPoint);
		this.repositionLedPoints();
	}

	private calculateLedPointsAvailableSpaces(availableHeight: number) {
		const usableMin = -availableHeight / 2;
		const usableMax = availableHeight / 2;

		const availableSpaces: LedAvailableSpace[] = [];

		const ranges = [
			...this.objectsManager.posts,
			...this.objectsManager.accessories.filter(
				(accessory) => !accessory.hostSlot
			),
		].map((obj) => ({
			min: obj.collisionBox.min.y,
			max: obj.collisionBox.max.y,
		}));

		ranges.sort((a, b) => a.min - b.min);

		const merged = [];

		for (const range of ranges) {
			const last = merged[merged.length - 1];
			if (!last || range.min > last.max) merged.push({ ...range });
			else last.max = Math.max(last.max, range.max);
		}

		if (merged.length === 0 || merged[0].min > usableMin)
			availableSpaces.push({
				topY: merged[0]?.min ?? usableMax,
				bottomY: usableMin,
			});

		for (let i = 0; i < merged.length - 1; i++) {
			const topY = merged[i + 1].min;
			const bottomY = merged[i].max;
			if (topY > bottomY) {
				availableSpaces.push({ topY, bottomY });
			}
		}

		const lastMax = merged[merged.length - 1]?.max;
		if (lastMax !== undefined && lastMax < usableMax)
			availableSpaces.push({ topY: usableMax, bottomY: lastMax });

		return availableSpaces;
	}

	repositionLedPoints() {
		if (!this.autoPositioning) return;

		const upperPost = this.objectsManager.posts.find(
				(post) => post.type === PostType.UPPER
			),
			lowerPost = this.objectsManager.posts.find(
				(post) => post.type === PostType.LOWER
			),
			ledPoint = this.ledPoints[0];

		if (!upperPost || !lowerPost || !ledPoint) return;

		const availableHeight = new Vector3(
			0,
			upperPost.collisionBox.min.y,
			0
		).distanceTo(new Vector3(0, lowerPost.collisionBox.max.y, 0));

		const availableSpaces =
			this.calculateLedPointsAvailableSpaces(availableHeight);

		const spacesWithLedPoints: (LedAvailableSpace & { points: LedPoint[] })[] =
			availableSpaces.map((space) => ({
				...space,
				points: [],
			}));

		for (let index = 0; index < this.ledPoints.length; index++) {
			const ledPoint = this.ledPoints[index];
			spacesWithLedPoints[index % spacesWithLedPoints.length].points.push(
				ledPoint
			);
		}

		const accessorySize = new Vector3();
		ledPoint.collisionBox.getSize(accessorySize);

		for (const spaceWithLedPoints of spacesWithLedPoints) {
			const { bottomY, topY, points } = spaceWithLedPoints;
			const totalHeight = points.length * accessorySize.y;
			const availableSpace = topY - bottomY;

			const spacing = (availableSpace - totalHeight) / (points.length + 1);

			let currentY = bottomY + spacing + accessorySize.y / 2;

			for (let ledPoint of points) {
				ledPoint.posY = currentY;

				this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
					id: ledPoint.id,
					posY: Math.round(
						(currentY + this.renderer.handleManager.originToYCenterConst) * 1000
					),
				});

				currentY += accessorySize.y + spacing;
			}
		}
	}

	setLedTapesQuantity(quantity: number) {
		const diff = this.ledTapes.length - quantity;
		if (!diff) return;

		if (diff > 0)
			for (let i = 0; i < diff; i++) {
				const ledTape = this.ledTapes.pop();
				if (ledTape) ledTape.dispose();
			}

		if (diff < 0)
			for (let i = 0; i < -diff; i++) {
				const ledTape = this.ledTapes[0];
				if (!ledTape) continue;

				const newLedTape = ledTape.clone();
				this.ledTapes.push(newLedTape);
			}
	}

	removeLedPoint(id: UUID) {
		const ledPoint = this.ledPoints.find((ledPoint) => ledPoint.id === id);
		if (!ledPoint) return;

		ledPoint.dispose();
		this.ledPoints = this.ledPoints.filter((ledPoint) => ledPoint.id !== id);

		this.repositionLedPoints();
	}

	removeLedTapes() {
		for (const ledTape of this.ledTapes) {
			ledTape.dispose();
		}
		this.ledTapes = [];
	}

	removeLedPoints() {
		for (const ledPoint of this.ledPoints) {
			ledPoint.dispose();
		}
		this.ledPoints = [];
	}

	clampLedPointsPositions() {
		const upperPost = this.objectsManager.posts.find(
				(post) => post.type === PostType.UPPER
			),
			lowerPost = this.objectsManager.posts.find(
				(post) => post.type === PostType.LOWER
			);
		if (!upperPost || !lowerPost) return;

		const availableBox = new Box3();

		availableBox.min.set(-1, lowerPost.collisionBox.max.y, -1);
		availableBox.max.set(1, upperPost.collisionBox.min.y, 1);

		for (const ledPoint of this.ledPoints) {
			const accessorySize = new Vector3();
			ledPoint.collisionBox.getSize(accessorySize);

			availableBox.max.y -= accessorySize.y / 2;
			availableBox.min.y += accessorySize.y / 2;

			const newPos = new Vector3(),
				currentPos = new Vector3(0, ledPoint.posY, 0);

			availableBox.clampPoint(currentPos, newPos);

			availableBox.max.y += accessorySize.y / 2;
			availableBox.min.y -= accessorySize.y / 2;

			const hasChanged = ledPoint.posY !== newPos.y;

			if (!hasChanged) continue;

			this.renderer.emitter.emit(ThreeEvent.UPDATE_OBJECT_POS_Y, {
				id: ledPoint.id,
				posY: Math.round(newPos.y * 1000),
			});
			ledPoint.posY = newPos.y;
		}

		this.objectsManager.checkCollisions();
	}

	clear() {
		for (const ledTape of this.ledTapes) {
			ledTape.dispose();
		}
		this.ledTapes = [];

		for (const ledPoint of this.ledPoints) {
			ledPoint.dispose();
		}
		this.ledPoints = [];
	}
}
